import ProspectService from "@/src/api/ProspectService.js";
import DashboardService from "@/src/api/DashboardService.js";
import { isProspectV2Enabled } from "@/src/utils/utils.js";
import { ENRICH_FIELDS, REQUEST_ID_ALIASES } from "@/src/constants/prospectConstants.js";

const normalizeEnrichmentResponse = (response) => {
  if (!response) return response;

  const normalized = { ...response };

  if (!normalized.request_id) {
    for (const alias of REQUEST_ID_ALIASES) {
      if (normalized[alias]) {
        normalized.request_id = normalized[alias];
        break;
      }
    }
  }

  return normalized;
};

const transformToOldPayload = (newPayload) => {
  const oldPayload = {
    target: newPayload.target || 1,
  };

  if (newPayload.ids && newPayload.ids.length > 0) {
    oldPayload.selected_id = newPayload.ids[0];
  }

  if (newPayload.enrich_fields) {
    oldPayload.reveal_email = newPayload.enrich_fields.includes(ENRICH_FIELDS.EMAIL);
    oldPayload.reveal_phone_number = newPayload.enrich_fields.includes(ENRICH_FIELDS.PHONE);
  } else {
    oldPayload.reveal_email = false;
    oldPayload.reveal_phone_number = false;
  }

  if (newPayload.list_id != null) {
    oldPayload.list_id = newPayload.list_id;
  }

  if (newPayload.sequence_id != null) {
    oldPayload.sequence_id = newPayload.sequence_id;
  }

  return oldPayload;
};

const transformToOldBulkPayload = (newPayload) => {
  const oldPayload = {
    target: newPayload.target || 1,
    selected_ids: newPayload.ids || [],
    unselected_ids: [],
    filters: newPayload.filters || {},
  };

  if (newPayload.list_id != null) {
    oldPayload.list_id = newPayload.list_id;
  }

  if (newPayload.sequence_id != null) {
    oldPayload.sequence_id = newPayload.sequence_id;
  }

  if (newPayload.sequence_entity_config_id != null) {
    oldPayload.sequence_entity_config_id = newPayload.sequence_entity_config_id;
  }

  return oldPayload;
};

const isBulkOperation = (payload) => {
  return (
    (payload.ids && payload.ids.length > 1) ||
    payload.filters != null ||
    payload.prompt != null
  );
};

export const enrichProspectLeads = async (payload) => {
  const useNewEndpoint = isProspectV2Enabled();

  let response;
  if (useNewEndpoint) {
    response = await ProspectService.enrichProspectLeads(payload);
  } else if (payload.lead_id) {
    const revealEmails = payload.enrich_fields?.includes(ENRICH_FIELDS.EMAIL) ?? false;
    const revealPhones = payload.enrich_fields?.includes(ENRICH_FIELDS.PHONE) ?? false;
    response = await DashboardService.enrichLeadDetails(payload.lead_id, revealEmails, revealPhones);
  } else if (isBulkOperation(payload)) {
    const oldPayload = transformToOldBulkPayload(payload);
    response = await DashboardService.addLeadsFromFind(oldPayload);
  } else {
    const oldPayload = transformToOldPayload(payload);
    response = await DashboardService.enrichAndAddLead(oldPayload);
  }

  return normalizeEnrichmentResponse(response);
};

export const enrichProspectCompanies = async (payload) => {
  const useNewEndpoint = isProspectV2Enabled();

  let response;
  if (useNewEndpoint) {
    response = await ProspectService.enrichProspectCompanies(payload);
  } else if (isBulkOperation(payload)) {
    const oldPayload = transformToOldBulkPayload(payload);
    response = await DashboardService.addCompaniesFromFind(oldPayload);
  } else {
    const oldPayload = transformToOldPayload(payload);
    response = await DashboardService.enrichAndAddCompany(oldPayload);
  }

  return normalizeEnrichmentResponse(response);
};

export const enrichProspectEntity = async (payload, entityType = "lead") => {
  if (entityType === "company") {
    return await enrichProspectCompanies(payload);
  } else {
    return await enrichProspectLeads(payload);
  }
};
